import { type NPC, type CombatStats } from 'src/types'

// ids for comparison
type StatID = 'str' | 'dex'
type SkillID = string

interface ModifierIDs {
  stats?: StatID[]
  skills?: SkillID[]
}

// actual values
interface Stats {
  str: number
  dex: number
}

type Skills = Record<string, number>

type Modifiers = {
  stats?: Stats
  skills?: Skills
}

// ============================ //

const rollDice = (times: number, sides: number) => {
  let total = 0

  for (let i = 0; i < times; i += 1) {
    total += Math.floor(Math.random() * sides + 1)
  }

  return total
}

// dnd formula
const calculateStatModifier = (stat: number) => stat // Math.floor((stat - 10)) is DnD version

const calculateModifier = (modifierIDs: ModifierIDs, modifiers: Modifiers) => {
  let modifier = 0
  // add all modifiers up
  if (modifierIDs.stats != null) {
    modifierIDs.stats.forEach(statID => {
      if ((modifiers.stats != null) && modifiers.stats[statID]) {
        modifier += calculateStatModifier(modifiers.stats[statID])
      }
    })
  }

  if (modifierIDs.skills != null) {
    modifierIDs.skills.forEach(skillID => {
      if ((modifiers.skills != null) && modifiers.skills[skillID]) {
        modifier += modifiers.skills[skillID]
      }
    })
  }
  return modifier
}

const attackRoll = (attackRoll: string) => {
  const splitIndex = attackRoll.indexOf('d')
  const times = Number(attackRoll.slice(0, splitIndex))
  const sides = Number(attackRoll.slice(splitIndex + 1))

  const rollValue = rollDice(times, sides)
  return rollValue
}

const challengeRoll = (target: number, modifier: number = 0) => {
  const roll = rollDice(1, 20)
  return target < roll + modifier
}

const createEnemy = (NPC: NPC, lvl: number) => {
  const vit = Math.floor(NPC.vit * lvl / 5)
  const str = Math.floor(NPC.str * lvl / 5)
  const dex = Math.floor(NPC.dex * lvl / 5)

  const hpRoll = (NPC.hp || vit) + rollDice(lvl, NPC.vit)
  const apRoll = (NPC.ap || vit) + rollDice(lvl, Math.ceil(NPC.vit / 2))
  
  const newEnemy: CombatStats = {
    id: NPC.id,
    lvl,
    hp: hpRoll,
    hp_max: hpRoll,
    ap: apRoll,
    ap_max: apRoll,
    vit,
    str,
    dex,
    karma: 0
  }
  return newEnemy
}

const calculateDamage = (attacker: CombatStats, defender: CombatStats, isDefending?: boolean) => {
  const atkStrModifier = 
  calculateModifier(
    { stats: ['str'] },
    { stats: attacker }
  )

  const atkDexModifier = 
  calculateModifier(
    { stats: ['dex'] },
    { stats: attacker }
  )

  const maxDamage = atkStrModifier
  const damageRoll = attackRoll(`1d${atkStrModifier}`)

  // defender takes 2x damage when exhausted
  const defAPModifier = defender.ap > 0 ? 1 : 2
  // attacker deals 1/2 damage when exhausted
  const atkAPModifier = attacker.ap > 0 ? 1 : 0.5

  const defenseMultiplier = isDefending ? 0.5 : 1
  const preDamage = Math.ceil((damageRoll > maxDamage ? maxDamage : damageRoll) * defenseMultiplier * defAPModifier * atkAPModifier)
  // the lowerBound is the dex modifier, but can never superceed str
  const lowerBound = 1 + (atkStrModifier < atkDexModifier ? atkStrModifier : atkDexModifier)
  // the final damage with the lowerBound
  const damage = preDamage > lowerBound ? preDamage : lowerBound

  return damage
}

export {
  calculateDamage,
  rollDice,
  attackRoll,
  challengeRoll,
  calculateModifier,
  createEnemy
}
